import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TaskDescription } from '../../components/tasks/TaskDescription';
import { Task, getTaskById } from '../../services/taskService';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';

export const TaskDescriptionPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTask = async () => {
    if (!taskId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const taskData = await getTaskById(taskId);
      setTask(taskData);
    } catch (err) {
      console.error('Error fetching task:', err);
      setError(t('tasks.fetchError', 'Impossible de récupérer les informations de la tâche'));
      toast({
        variant: 'destructive',
        title: t('tasks.fetchError', 'Erreur'),
        description: t('tasks.fetchErrorDesc', 'Impossible de récupérer les informations de la tâche'),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const handleEdit = () => {
    navigate(`/app/tasks/edit/${taskId}`);  // Mise à jour du chemin
  };

  const handleDelete = () => {
    // Cette fonctionnalité pourrait être ajoutée plus tard
    // Implémenter un modal de confirmation avant suppression
  };

  const handleGoBack = () => {
    navigate(-1); // Cette fonction retourne à la page précédente dans l'historique
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-20">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">{t('common.loading', 'Chargement...')}</p>
        </div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="py-10 px-4 text-center">
        <h2 className="text-xl font-semibold text-destructive mb-2">
          {t('tasks.error', 'Erreur')}
        </h2>
        <p className="text-muted-foreground mb-6">
          {error || t('tasks.notFound', 'Tâche non trouvée')}
        </p>
        <Button onClick={handleGoBack}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('common.back', 'Retour')}
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <Button 
        variant="ghost" 
        className="mb-6" 
        onClick={handleGoBack}
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        {t('common.back', 'Retour')}
      </Button>
      
      <TaskDescription 
        task={task} 
        onEdit={handleEdit}
        onDelete={handleDelete}  // Cette prop existe déjà dans votre code
        refreshTask={fetchTask}
      />
    </div>
  );
};

export default TaskDescriptionPage;
